import React from 'react';

import { client } from '@oysterjs/ais/api/client';
import { useEmailBulkDraftCardQuery } from '@oysterjs/ais/api/graphql';
import {
  isBulkDraftDrafting,
  type LiveBulkDraft
} from '@oysterjs/ais/components/workspaces/lib/bulk-email-draft-event';

/** How often a bulk draft's card reads it again while Cara is drafting it. */
export const BULK_DRAFT_POLL_MS = 15_000;

/**
 * A bulk draft as last read. Nothing tells the workspace when a drafting run ends, so it's read
 * again every 15 seconds while Cara drafts, and when the chat event that changed it ends, such
 * as a FILL setting its recipients drafting.
 */
export const useLiveBulkDraft = (
  bulkSendId: string | undefined,
  eventEndedAt: unknown
): LiveBulkDraft | undefined => {
  const { data, refetch, startPolling, stopPolling } = useEmailBulkDraftCardQuery({
    client,
    variables: { id: bulkSendId ?? '' },
    skip: !bulkSendId,
    // Otherwise the read when a FILL ends joins one sent before it and answers from before
    // the drafting started, so polling never starts.
    context: { queryDeduplication: false }
  });
  const bulkDraft = data?.emailBulkSend ?? undefined;
  const isDrafting = !!bulkDraft && isBulkDraftDrafting(bulkDraft);

  React.useEffect(() => {
    if (!isDrafting) return;
    startPolling(BULK_DRAFT_POLL_MS);
    return stopPolling;
  }, [isDrafting, startPolling, stopPolling]);

  const lastEventEndedAt = React.useRef(eventEndedAt);
  React.useEffect(() => {
    if (lastEventEndedAt.current === eventEndedAt) return;
    lastEventEndedAt.current = eventEndedAt;
    if (bulkSendId) {
      // The card keeps what it last read.
      refetch().catch(() => undefined);
    }
  }, [bulkSendId, eventEndedAt, refetch]);

  return bulkDraft;
};
